import React from "react";
import { ageFromDob } from "@/lib/age";

export const RELATIONS = [
  "Self",
  "Spouse",
  "Father",
  "Mother",
  "Son",
  "Daughter",
  "Brother",
  "Sister",
  "Grandfather",
  "Grandmother",
  "Other",
];

// ============== PLAN-TIER FAMILY RULES ==============
// Mirror of server.py validate_family_composition — keep in sync.

// Returns the list of allowed relations for a given plan tier.
// `members` is the proposed sub-member list (excludes self), each item with at
// least { relation } — used to dynamically hide options already at their cap.
export function allowedRelationsFor(planTier, members = []) {
  const tier = (planTier || "").toLowerCase();
  if (tier === "husband_wife") return ["Spouse"];

  // Default sub-member set (no constraint plans)
  const BASE = ["Spouse", "Father", "Mother", "Son", "Daughter"];

  if (tier === "family") {
    const rels = members.map((m) => (m.relation || "").trim().toLowerCase());
    const has = (r) => rels.includes(r);
    const list = [];
    if (!has("spouse")) list.push("Spouse");
    if (!has("father")) list.push("Father");
    if (!has("mother")) list.push("Mother");
    // Up to 2 children when other relatives are present
    const otherRelatives = has("spouse") || has("father") || has("mother");
    const childrenCount = rels.filter((r) => r === "son" || r === "daughter").length;
    if (otherRelatives) {
      if (childrenCount < 2) {
        list.push("Son", "Daughter");
      }
    } else {
      // No spouse/parents → any number of children (up to plan cap of 5 extras)
      list.push("Son", "Daughter");
    }
    return list;
  }

  return BASE;
}

// Returns an error string or null. `members` is the proposed list (excludes self).
export function validateFamilyComposition(planTier, members = []) {
  const tier = (planTier || "").toLowerCase();
  const rels = members.map((m) => (m.relation || "").trim().toLowerCase());

  if (tier === "husband_wife") {
    if (members.length > 1) return "Husband-Wife plan allows only your spouse — no other members.";
    for (const r of rels) if (r && r !== "spouse") return "Husband-Wife plan: only the Spouse can be added.";
    return null;
  }

  if (tier === "family") {
    const ALLOWED = new Set(["spouse", "son", "daughter", "father", "mother", ""]);
    for (const r of rels) if (r && !ALLOWED.has(r)) return "Family plan: only Spouse, Son, Daughter, Father, Mother are allowed.";
    if (members.length > 5) return "Family plan supports a maximum of 6 members (including you).";
    for (const [r, label] of [["spouse", "Spouse"], ["father", "Father"], ["mother", "Mother"]]) {
      if (rels.filter((x) => x === r).length > 1) return `Family plan: only one ${label} is allowed.`;
    }
    const childCount = rels.filter((x) => x === "son" || x === "daughter").length;
    if (childCount > 2) {
      const otherRelatives = rels.some((x) => x === "spouse" || x === "father" || x === "mother");
      if (otherRelatives) return "Family plan: to add more than 2 children, please remove Spouse / Father / Mother first.";
    }
    for (const m of members) {
      const r = (m.relation || "").trim().toLowerCase();
      if (r === "son" || r === "daughter") {
        const age = ageFromDob(m.dob || "");
        if (age !== null && age > 18) return `Family plan: children must be 18 or under (got ${age} years for ${m.full_name || r}).`;
      }
    }
  }
  return null;
}

/**
 * Mobile-friendly select for the relation field. Now plan-tier-aware: pass
 * `planTier` and `otherMembers` to dynamically restrict the dropdown.
 */
export function RelationSelect({ value, onChange, excludeSelf = false, testid, required = false,
                                  label = "Relation", className = "",
                                  planTier, otherMembers = [] }) {
  let opts;
  if (planTier) {
    opts = allowedRelationsFor(planTier, otherMembers);
    // Keep the currently selected value visible even if it would otherwise be hidden
    if (value && !opts.includes(value)) opts = [value, ...opts];
  } else {
    opts = excludeSelf ? RELATIONS.filter((r) => r !== "Self") : RELATIONS;
  }
  return (
    <div className={className}>
      <label className="text-xs font-medium text-[#5C6B7A] block mb-1">{label}{required ? " *" : ""}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-[#E1E8F0] bg-white px-3 py-2.5 outline-none focus:border-[#1E4F87] text-sm"
      >
        <option value="">Select relation…</option>
        {opts.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
    </div>
  );
}
